import { createRoutine } from 'redux-saga-routines';
import { isEmpty, isPlainObject } from 'lodash';
import actionTypes from 'constants/action-types';

/**
 * Action creator for fetching the dashboard items for a specific dashboard.
 *
 * @param {Object} query - A MongoDB query selector to filter by. Defaults to {} which does not filter results by any criterion.
 * @return {Object} - the redux saga routine
 *
 * @see https://docs.mongodb.com/stitch/mongodb/actions/collection.find/#collection.find
 **/
export const fetchDashboardItems = createRoutine(
  actionTypes.DASHBOARD_ITEMS.READ,
  payload => ({ ...payload })
);

/**
 * Action creator for syncing a single dashboard item.
 *
 * @param {Object} dashboardItemId - An object containing the ID of the dashboard item to sync in the dashboard items
 * @return {Object} - the redux saga routine
 */
export const syncDashboardItem = createRoutine(
  actionTypes.DASHBOARD_ITEMS.SYNC
);

/**
 * Action creator to insert or update the dashboard item locally and in the database.
 *
 * If a dashboard item with the given _id is already present in the collection, update the values
 * according to the provided object. If the _id is not present in the collection, insert a new
 * dashboard item into the 'metadata.items' collection.
 *
 * @param {Object} dashboardItem - the dashboard item to upsert.
 * @return {Object} - the redux saga routine
 **/
export const upsertDashboardItem = createRoutine(
  actionTypes.DASHBOARD_ITEMS.UPSERT
);

/**
 * Action creator to duplicate all the dashboard items to the specified dashboard.
 * This action is dispatched while duplicating a dashboard.
 */
export const duplicateDashboardItems = createRoutine(
  actionTypes.DASHBOARD_ITEMS.DUPLICATE
);

/**
 * Deletes the provided dashboard items in the database. On error, it sets the
 * `errorMessage` state to the returned message string. This action should also
 * trigger an upsert on the associated dashboard, removing the item from the
 * dashboard's layout array.
 *
 * @param {Object} querySelector - the query selector to delete documents
 * @return {Object} - the redux saga routine
 **/
export const deleteDashboardItems = createRoutine(
  actionTypes.DASHBOARD_ITEMS.DELETE,
  payload => {
    // here we make sure that if the payload is an object, it's non-empty so we
    // don't accidentally delete all items in the collection.
    if (isPlainObject(payload) && isEmpty(payload)) {
      throw new Error(
        'payload for deleteDashboardItems action creator is an empty object. This is not allowed.'
      );
    }
    return payload;
  }
);

/**
 * Action creator triggered when creating a new dashboard item or editing an existing dashboard item.
 * Used to populate the currentBuildItem reducer. The payload represents the ID of the dashboard item.
 *
 * When creating a new dashboard item (payload is null), a saga will generate a new UUID for the item
 * that is used by the itemDataCacheReducer to reduce the data associated with the item's data source.
 *
 * @param {String|Null} dashboardItemId - the ID of the dashboard item to edit. Null when creating a new dhasboard.
 * @return {Object} - the redux saga routine
 **/
export const editDashboardItem = createRoutine(
  actionTypes.DASHBOARD_ITEMS.BUILDER.EDIT
);

/**
 * Action creator triggered when saving a dashboard item from the builder.
 * At this stage the data can be validated and a saga listening for this action in turn triggers an
 * upsertDashboardItem action, and waits for a success from that action before triggering
 * a route change back to the dashboard view that this item is associated with.
 *
 * @return {Object} - the redux saga routine
 **/
export const saveDashboardItem = createRoutine(
  actionTypes.DASHBOARD_ITEMS.BUILDER.SAVE,
  payload => payload,
  payload =>
    payload && payload.dashboardId
      ? { dashboardId: payload.dashboardId }
      : undefined
);

/**
 * Action creator triggered when discarding the changes to a new or existing dashboard item.
 * Triggers a redux-saga as a side-effect to push a route change to the history state.
 *
 * @param {String} dashboardId - the dashboardId to auto navigate to following the cancel. Used in the action meta.
 * @return {Object} - the redux action
 **/
export const discardChangesToDashboardItem = dashboardId => {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CANCEL,
    meta: { dashboardId },
  };
};

/**
 * Action creator for resetting the dashboard item builder's reducers to their initial state.
 * Unlike the `discardChangesToDashboardItem` or `saveDashboardItem` action creators,
 * this action-creator does not trigger a side-effect in the form of a route change.
 *
 * @return {Object} - the redux action
 **/
export const resetDashboardItemBuilder = () => {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.RESET,
  };
};

/**
 * Action creator triggered when choosing a data source from the choose data source drop down.
 * Used to populate the dataSourceId in the currentBuildItem reducer.
 *
 * @param {String} dataSourceId - the _id of the data source chosen.
 * @return {Object} - the redux action
 */
export const setDataSourceForCurrentBuildItem = dataSourceId => {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.SET_DATA_SOURCE,
    payload: dataSourceId,
  };
};

/**
 * Action creator triggered when setting the title of the dashboard item.
 * Used to populate the title in the currentBuildItem reducer.
 *
 * @param {String} title - the title for the dashboard item.
 * @return {Object} - the redux action
 **/
export const setTitleForCurrentBuildItem = title => {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.SET_TITLE,
    payload: title,
  };
};

/**
 * Action creator triggered when manually retrying to render the dashboard item preview.
 * Normally called when the user clicks the "Retry" button following a render error.
 * Triggers `updateChartSaga` as a side-effect.
 *
 * @return {Object} - the redux action
 **/
export const retryRenderDashboardItemBuilderPreview = () => {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.RETRY_RENDER_PREVIEW,
  };
};

/**
 * Action creator for saving embedding settings for a dashboardItem.
 *
 * @return {Object} - the redux saga routine
 */
export const embedDashboardItem = createRoutine(
  actionTypes.DASHBOARD_ITEMS.EMBED
);

/**
 * Action creator for adding dashboardItem with dashboardItemId to the dashboard with dashboardId
 * via the 'move to' dialog
 *
 * @param {String} dashboardItemId - _id of the dashboard item to move.
 * @param {String} dashboardId - it _id of the dashboard to move.
 * @return {Object} - the redux saga routine
 */
export const moveDashboardItem = createRoutine(
  actionTypes.DASHBOARD_ITEMS.MOVE
);

export default {
  fetchDashboardItems,
  syncDashboardItem,
  deleteDashboardItems,
  duplicateDashboardItems,
  upsertDashboardItem,
  editDashboardItem,
  saveDashboardItem,
  discardChangesToDashboardItem,
  resetDashboardItemBuilder,
  setDataSourceForCurrentBuildItem,
  retryRenderDashboardItemBuilderPreview,
  embedDashboardItem,
  moveDashboardItem,
};
